import { useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ArrowLeftRight, ScanLine, Trash2, Send, FileText } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { useDocViewer } from '@/components/ui/useDocViewer';
import { FilterField } from '@/components/ui/FilterBar';
import { Input } from '@/components/form/Input';
import { SearchableSelect, type SearchableOption } from '@/components/form/SearchableSelect';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { formatDate } from '@/lib/format';
import { lookupDeptSerial, listDeptTransfers, listDeptLocations, createDeptTransfer, type DeptTransferRow } from '@/api/deptTransfer';

interface Scanned { serialNo: string; itemCode: string; itemName: string; qty: number; dept: string | null; lastBin: string | null; toCode: string | null }

export function DeptTransferPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const docViewer = useDocViewer();
  const [direction, setDirection] = useState<'Out' | 'In'>('Out');
  const [department, setDepartment] = useState('');
  const [remarks, setRemarks] = useState('');
  const [scan, setScan] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanned, setScanned] = useState<Scanned[]>([]);
  const [locMode, setLocMode] = useState<'same' | 'new'>('same');
  const [locCode, setLocCode] = useState('');
  const scanRef = useRef<HTMLInputElement>(null);

  const transfersQ = useQuery({ queryKey: ['dept-transfers'], queryFn: () => listDeptTransfers() });
  const locationsQ = useQuery({ queryKey: ['dept-transfer-locations'], queryFn: () => listDeptLocations(), enabled: direction === 'In' });

  const loading = transfersQ.isFetching && !transfersQ.data;

  const binOptions = useMemo<SearchableOption[]>(
    () => (locationsQ.data ?? []).map((l) => ({
      value: l.code,
      label: l.code,
      sublabel: `${l.name}${l.free != null ? ` · ${l.free} free` : ''}`,
    })),
    [locationsQ.data],
  );

  async function addSerial() {
    const sn = scan.trim();
    if (!sn || scanning) return;
    if (scanDisabled) { toast.push('Select a bin first', 'error'); return; }
    if (scanned.some((s) => s.serialNo.toUpperCase() === sn.toUpperCase())) { toast.push('Serial already scanned', 'error'); setScan(''); return; }
    setScanning(true);
    try {
      const s = await lookupDeptSerial(sn);
      if (direction === 'Out' && s.status !== 'InWarehouse') {
        toast.push(`"${sn}" is "${s.status}", not in warehouse`, 'error'); return;
      }
      if (direction === 'In' && s.status !== 'AtDepartment') {
        toast.push(`"${sn}" is "${s.status}", not out at a department`, 'error'); return;
      }
      if (direction === 'In' && !department.trim() && s.department) setDepartment(s.department);
      // New-bin rows are seeded with the current default bin; each row can then
      // be re-pointed independently. Same-bin rows carry no code (→ previous bin).
      const toCode = direction === 'In' && locMode === 'new' ? (locCode || null) : null;
      setScanned((p) => [...p, { serialNo: s.serialNo, itemCode: s.itemCode, itemName: s.itemName, qty: s.qty, dept: s.department, lastBin: s.lastBin ? `${s.lastBin.code} — ${s.lastBin.name}` : null, toCode }]);
    } catch (e) {
      toast.push(apiError(e, 'Serial not found'), 'error');
    } finally {
      setScanning(false);
      setScan('');
      scanRef.current?.focus();
    }
  }

  const save = useMutation({
    mutationFn: () =>
      createDeptTransfer({
        direction,
        department: department.trim(),
        remarks: remarks.trim() || undefined,
        items: scanned.map((s) => ({ serialNo: s.serialNo, ...(direction === 'In' && s.toCode ? { locationCode: s.toCode } : {}) })),
      }),
    onSuccess: (r) => {
      toast.push(`${r.transferNo} · ${r.units} unit(s) ${direction === 'Out' ? 'issued to' : 'returned from'} ${department}`, 'success');
      qc.invalidateQueries({ queryKey: ['dept-transfers'] });
      qc.invalidateQueries({ queryKey: ['warehouse-stock'] });
      qc.invalidateQueries({ queryKey: ['dept-transfer-locations'] });
      setScanned([]); setRemarks(''); setScan(''); setLocCode('');
    },
    onError: (e: unknown) => toast.push(apiError(e, 'Transfer failed'), 'error'),
  });

  const totalPieces = scanned.reduce((s, x) => s + x.qty, 0);
  const needsBin = direction === 'In' && locMode === 'new' && !locCode.trim();
  // New bin: pick the bin first — that field is the live scan target until it's
  // chosen, then scanning moves to the serial field.
  const binIsScanTarget = needsBin;
  const scanDisabled = needsBin;
  const canSave = department.trim() !== '' && scanned.length > 0 && !needsBin && !save.isPending;

  return (
    <div>
      <SectionHeader
        title="Department Transfer"      />

      <div className="mb-4 overflow-hidden rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] backdrop-blur-xl sm:[&_input]:h-8 sm:[&_input]:py-1 sm:[&_input]:text-xs">
        <div className="flex min-h-[44px] items-center justify-between gap-2 border-b border-[var(--border-color)] px-3 py-2 sm:px-4">
          <span className="flex min-w-0 items-center gap-2 truncate text-sm font-extrabold">
            <ArrowLeftRight size={14} className="text-accent" /> New transfer note
          </span>
          <div className="flex shrink-0 items-center gap-3">
            {scanned.length > 0 && (
              <span className="hidden text-xs text-[var(--text-muted)] sm:inline">{scanned.length} unit{scanned.length === 1 ? '' : 's'} · {totalPieces} pcs</span>
            )}
            <Button variant="primary" size="sm" icon={<Send size={13} />} disabled={!canSave} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : direction === 'Out' ? 'Issue to Department' : 'Return to Warehouse'}
            </Button>
          </div>
        </div>

        {/* Direction on its own line; department + remarks below it with inline labels, like the filter bars. */}
        <div className="flex flex-col gap-2 px-4 py-3 sm:gap-3">
          <div className="flex w-fit gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-0.5">
            {([['Out', 'Out to department'], ['In', 'In from department']] as const).map(([d, label]) => (
              <button
                key={d}
                onClick={() => { setDirection(d); setScanned([]); setScan(''); setLocMode('same'); setLocCode(''); setTimeout(() => scanRef.current?.focus(), 0); }}
                className={clsx(
                  'rounded-md px-3 py-1.5 text-xs font-bold transition',
                  direction === d ? 'bg-accent text-white shadow-sm' : 'text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text-primary)]',
                )}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-3">
            <FilterField label="Department" className="sm:w-[340px]">
              <Input value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="e.g. Packaging, Labeling" />
            </FilterField>
            <FilterField label="Remarks" className="sm:min-w-[340px] sm:flex-1">
              <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Challan reference / reason (optional)" />
            </FilterField>
          </div>
        </div>

        {/* Placement (In only) */}
        {direction === 'In' && (
          <div className="border-t border-[var(--border-color)] px-4 py-3">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <span className="shrink-0 whitespace-nowrap text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Place returns in</span>
              <div className="flex w-fit gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-0.5">
                {([['same', 'Same bin as before'], ['new', 'New bin']] as const).map(([m, label]) => (
                  <button
                    key={m}
                    onClick={() => setLocMode(m)}
                    className={clsx(
                      'rounded-md px-3 py-1.5 text-xs font-bold transition',
                      locMode === m ? 'bg-accent text-white shadow-sm' : 'text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text-primary)]',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {locMode === 'new' && (
                <div className="flex items-center gap-2">
                  <div className={clsx('w-full rounded-lg sm:max-w-xs', binIsScanTarget && 'ring-2 ring-accent ring-offset-2 ring-offset-[var(--card-bg)]')}>
                    <SearchableSelect
                      options={binOptions}
                      value={locCode}
                      onChange={(v) => { setLocCode(String(v)); setTimeout(() => scanRef.current?.focus(), 0); }}
                      placeholder="Scan or search bin…"
                      leftIcon={<ScanLine size={13} />}
                    />
                  </div>
                  {binIsScanTarget ? (
                    <span className="hidden shrink-0 items-center gap-1 text-[11px] font-bold text-accent sm:flex">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-accent" /> scan bin
                    </span>
                  ) : (
                    <span className="hidden shrink-0 text-[11px] text-[var(--text-muted)] sm:block">default for new scans</span>
                  )}
                </div>
              )}
            </div>
            {locMode === 'same' && (
              <p className="mt-1.5 text-[11px] text-[var(--text-muted)]">Scan each unit below — its previous bin appears in the “Returns to” column, and it goes back there.</p>
            )}
            {locMode === 'new' && !binIsScanTarget && (
              <p className="mt-1.5 text-[11px] text-[var(--text-muted)]">New scans go to this bin. To split, change any row’s bin in the “Returns to” column below — or switch the bin here and scan the next group.</p>
            )}
          </div>
        )}

        {/* Scan */}
        <div className="border-t border-[var(--border-color)] px-4 py-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
            <FilterField label="Scan" className="sm:w-[460px]">
              <div className={clsx('relative rounded-lg', !scanDisabled && 'ring-2 ring-accent ring-offset-2 ring-offset-[var(--card-bg)]')}>
                <ScanLine size={13} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                <Input
                  ref={scanRef}
                  autoFocus
                  disabled={scanDisabled}
                  value={scan}
                  onChange={(e) => setScan(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSerial(); } }}
                  placeholder={scanDisabled ? 'Select a bin above first…' : 'Scan / type serial…'}
                  className="!pl-7"
                />
              </div>
            </FilterField>
            <Button variant="glass" size="sm" className="w-full justify-center sm:w-20 sm:shrink-0" onClick={addSerial} disabled={scanDisabled || !scan.trim() || scanning}>
              {scanning ? 'Adding…' : 'Add'}
            </Button>
            <span className="text-[11px] text-[var(--text-muted)]">
              {direction === 'Out' ? 'In-warehouse items' : 'Items out at a department'} · adds automatically on scan
              {scanDisabled && <span className="font-bold text-warning"> · select a bin first</span>}
            </span>
          </div>

          {scanned.length > 0 && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[560px] text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-black uppercase tracking-wider text-accent">
                    <th className="py-1">Serial</th>
                    <th className="py-1">Item</th>
                    <th className="py-1 text-right">Qty</th>
                    {direction === 'In' && <th className="py-1">At dept</th>}
                    {direction === 'In' && <th className="py-1">Returns to</th>}
                    <th className="py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {scanned.map((s, i) => (
                    <tr key={s.serialNo} className="border-t border-[var(--border-color)]/50">
                      <td className="py-1 font-semibold">{s.serialNo}</td>
                      <td className="py-1">{s.itemName || s.itemCode}</td>
                      <td className="py-1 text-right font-bold">{s.qty}</td>
                      {direction === 'In' && <td className="py-1 text-[var(--text-muted)]">{s.dept ?? '—'}</td>}
                      {direction === 'In' && (
                        <td className="py-1 font-semibold text-accent">
                          {locMode === 'new' ? (
                            <div className="w-52 max-w-[13rem]">
                              <SearchableSelect
                                options={binOptions}
                                value={s.toCode ?? ''}
                                onChange={(v) => setScanned((p) => p.map((r, x) => (x === i ? { ...r, toCode: v ? String(v) : null } : r)))}
                                placeholder="same as before"
                                leftIcon={<ScanLine size={12} />}
                              />
                            </div>
                          ) : (s.lastBin ?? <span className="text-warning">unassigned</span>)}
                        </td>
                      )}
                      <td className="py-1 text-right">
                        <button onClick={() => setScanned((p) => p.filter((_, x) => x !== i))} className="rounded p-1 text-[var(--text-muted)] hover:bg-danger/10 hover:text-danger" aria-label="Remove">
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Table<DeptTransferRow>
        storageKey="dept-transfers"
        paginated
        title="Transfers"
        count={`${transfersQ.data?.length ?? 0}`}
        rows={transfersQ.data ?? []}
        rowKey={(r) => r.id}
        loading={loading}
        columns={[
          { key: 'no', header: 'Transfer No', cell: (r) => <span className="font-extrabold text-accent">{r.transferNo}</span> },
          { key: 'dir', header: 'Direction', cell: (r) => <Badge tone={r.direction === 'Out' ? 'danger' : 'success'}>{r.direction === 'Out' ? 'Out ▸ Dept' : 'In ◂ Dept'}</Badge> },
          { key: 'dept', header: 'Department', cell: (r) => <span className="font-bold">{r.department}</span> },
          { key: 'date', header: 'Date', cell: (r) => formatDate(r.transferDate) },
          { key: 'units', header: 'Units', align: 'right', cell: (r) => r.items.length },
          { key: 'qty', header: 'Pcs', align: 'right', cell: (r) => <strong>{r.totalQty}</strong> },
          { key: 'by', header: 'By', cell: (r) => r.createdBy || '—' },
          {
            key: 'actions',
            header: '',
            align: 'right',
            cell: (r) => (
              <Button variant="glass" size="sm" onClick={() => docViewer.open('dept-transfer', r.id, r.transferNo)} title="Open A4 transfer note">
                <FileText size={14} />
              </Button>
            ),
          },
        ]}
      />
    </div>
  );
}
